import React, { useEffect, useState } from "react";
import { useI18n } from "../../lib/i18n";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from "../ui/command";
import { Sparkle, Share, Heartbeat, NotePencil } from "@phosphor-icons/react";
import { flattenEntities } from "../../lib/gamecraft";
import { studioHealth } from "../../lib/studioHealth";

export default function CommandPalette({ open, onOpenChange, tabs, project, onJump, onShare }) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const allEntities = flattenEntities(project).slice(0, 40);
  const notes = (project.notes || []).slice(0, 16);
  const health = studioHealth(project);
  const tabLabel = (tab) => (typeof tab.i18n === "string" && tab.i18n.startsWith("ide.") ? t(tab.i18n) : tab.i18n);

  const jump = (tab) => { onJump(tab); onOpenChange(false); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title={t("common.search")} description={t("cmdk.placeholder")}>
      <CommandInput
        placeholder={t("cmdk.placeholder")}
        value={query}
        onValueChange={setQuery}
        data-testid="cmdk-input"
      />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>{t("cmdk.empty")}</CommandEmpty>
        <CommandGroup heading={t("cmdk.tabs")}>
          {tabs.map((tab) => (
            <CommandItem
              key={tab.key}
              value={`tab ${tab.key} ${tabLabel(tab)}`}
              onSelect={() => jump(tab.key)}
              data-testid={`cmdk-tab-${tab.key}`}
            >
              <tab.icon size={14} weight="duotone" className="mr-2 text-cyan-400" />
              <span>{tabLabel(tab)}</span>
              <span className="ml-auto text-[10px] tracking-[0.2em] uppercase text-zinc-500">{tab.key}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {allEntities.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("cmdk.entities")}>
              {allEntities.slice(0, 30).map((e) => (
                <CommandItem
                  key={`${e.kind}-${e.id}`}
                  value={`entity ${e.name} ${e.kind} ${e.subtype || ""} ${(e.tags || []).join(" ")}`}
                  onSelect={() => jump("ideas")}
                  data-testid={`cmdk-entity-${e.id}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mr-2" />
                  <span>{e.name}</span>
                  <span className="ml-auto text-[10px] tracking-[0.2em] uppercase text-zinc-500">{e.kind}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {(project.sprites || []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("cmdk.sprites")}>
              {(project.sprites || []).slice(0, 20).map((s) => (
                <CommandItem
                  key={s.id}
                  value={`sprite ${s.name} ${s.subtype || ""} ${(s.tags || []).join(" ")} ${s.category || ""}`}
                  onSelect={() => jump("sprites")}
                  data-testid={`cmdk-sprite-${s.id}`}
                >
                  <div className="w-5 h-5 bg-zinc-950 border border-zinc-800 mr-2 flex items-center justify-center">
                    <img src={s.data_url} alt="" className="max-w-full max-h-full" style={{ imageRendering: "pixelated" }} />
                  </div>
                  <span>{s.name}</span>
                  {(s.subtype || s.category) && <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">{s.subtype || s.category}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {(project.samples || []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Audio">
              {(project.samples || []).slice(0, 16).map((s) => (
                <CommandItem
                  key={s.id}
                  value={`audio ${s.name} ${s.tag || ""} ${s.category || ""}`}
                  onSelect={() => jump("audio")}
                >
                  <span>{s.name}</span>
                  <span className="ml-auto text-[10px] tracking-[0.2em] uppercase text-zinc-500">{s.tag || s.category}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {(project.files || []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t("cmdk.files")}>
              {(project.files || []).map((f) => (
                <CommandItem
                  key={f.id}
                  value={`file ${f.path}`}
                  onSelect={() => jump("code")}
                  data-testid={`cmdk-file-${f.id}`}
                >
                  <span className="text-zinc-600 mr-2">/</span>
                  <span className="font-mono text-xs">{f.path}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading="Siguiente paso">
          {health.next.map((n) => (
            <CommandItem key={n.label} value={`salud ${n.label} ${n.tab}`} onSelect={() => jump(n.tab)}>
              <Heartbeat size={14} className="mr-2 text-cyan-400" />
              <span>{n.label}</span>
              <span className="ml-auto text-[10px] uppercase text-zinc-500">{n.tab}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {notes.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Notas">
              {notes.map((n) => (
                <CommandItem
                  key={n.id}
                  value={`nota ${n.title} ${n.body || ""} ${(n.links || []).map((l) => l.label).join(" ")}`}
                  onSelect={() => jump("notes")}
                >
                  <NotePencil size={14} className="mr-2 text-cyan-400" />
                  <span className="truncate">{n.title || "Sin título"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading={t("cmdk.actions")}>
          <CommandItem value="ai generate ideas assistant" onSelect={() => jump("ai")} data-testid="cmdk-action-ai">
            <Sparkle size={14} weight="fill" className="mr-2 text-cyan-400" />
            <span>{lang_label_run_ai(t)}</span>
          </CommandItem>
          <CommandItem value="share project" onSelect={() => { onShare?.(); onOpenChange(false); }} data-testid="cmdk-action-share">
            <Share size={14} className="mr-2 text-cyan-400" />
            <span>{t("ide.share")}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

function lang_label_run_ai(t) {
  return t("ai.send") + " — " + t("ai.title");
}
